import type { InputCaption, ResyncOptions, SubtitleCaption } from "./types";
import { toMilliseconds } from "./utils/time";

/**
 * Resyncs subtitle captions by applying a time offset
 */
export const resync = (captions: InputCaption[], options: ResyncOptions): SubtitleCaption[] => {
  const { offset, preventNegativeStart = true } = options;

  if (!Array.isArray(captions)) {
    throw new Error("Captions must be an array");
  }

  if (typeof offset !== "number") {
    throw new Error("Offset must be a number");
  }

  return captions.map((caption, index) => {
    // Validate caption
    if (!caption || typeof caption.text !== "string") {
      throw new Error(`Invalid caption at index ${index}: missing or invalid text`);
    }

    // Convert times to milliseconds if they're strings
    const startMs =
      typeof caption.start === "string" ? toMilliseconds(caption.start) : caption.start;
    const endMs = typeof caption.end === "string" ? toMilliseconds(caption.end) : caption.end;

    // Validate times
    if (typeof startMs !== "number" || typeof endMs !== "number") {
      throw new Error(`Invalid caption at index ${index}: invalid start or end time`);
    }

    // Apply offset
    let newStart = startMs + offset;
    let newEnd = endMs + offset;

    // Prevent negative start times if requested
    if (preventNegativeStart && newStart < 0) {
      const adjustment = -newStart;
      newStart = 0;
      newEnd = Math.max(0, newEnd + adjustment);
    }

    // Ensure end time is after start time
    if (newEnd <= newStart) {
      newEnd = newStart + 1000; // Add 1 second if end would be before start
    }

    const resyncedCaption: SubtitleCaption = {
      start: newStart,
      end: newEnd,
      text: caption.text,
    };

    // Preserve settings if present
    if (caption.settings) {
      resyncedCaption.settings = caption.settings;
    }

    return resyncedCaption;
  });
};

/**
 * Resyncs subtitle captions with a simple offset (legacy API compatibility)
 */
export const resyncSimple = (captions: InputCaption[], offset: number): SubtitleCaption[] => {
  return resync(captions, { offset });
};
